/**
 * Progressive literacy renderer. Turns a word/value into a partially-
 * readable form based on how many individual letters the blob has
 * learned.
 *
 * Letters unlock in English frequency order — e first, then t, a, o,
 * and so on. Each letter costs one unit of `literacy` (DNA category).
 * Mixed case is collapsed to lowercase for reveal comparison so "E"
 * and "e" unlock together; the original case is preserved on output.
 *
 * Numbers unlock as a group at a higher literacy threshold (since
 * reading digits is a separate acquired skill). Below that the blob
 * gets approximation words — 'little', 'some', 'much'.
 *
 * Pure logic. No Phaser dependency.
 */
/**
 * Perception tiers — as the blob's mind organizes, the chaos of
 * symbols narrows before any letter becomes legible. What starts as
 * alien noise resolves first into structured punctuation, then into
 * plain marks, before the first real letter arrives.
 */
const SYMBOLS_CHAOS = '@#$%&*~^+='.split('');
const SYMBOLS_STRUCTURED = '*+-:;?'.split('');
const SYMBOLS_MARKS = '.-_|'.split('');

const CHAOS_THRESHOLD = 0.3;     // below: full chaos
const STRUCTURED_THRESHOLD = 0.7; // below: structured noise
// above STRUCTURED_THRESHOLD: simple marks until first letter at LETTER_COST

/** English letter frequency (most-common first). */
export const LETTER_ORDER = [
  'e', 't', 'a', 'o', 'i', 'n', 's', 'h', 'r', 'd',
  'l', 'c', 'u', 'm', 'w', 'f', 'g', 'y', 'p', 'b',
  'v', 'k', 'j', 'x', 'q', 'z',
];

/** Literacy threshold at which digits become legible. */
export const NUMERAL_THRESHOLD = 12;

/** Each known letter costs this much literacy. */
export const LETTER_COST = 1;

function symbolPoolFor(literacy: number): string[] {
  if (literacy < CHAOS_THRESHOLD) return SYMBOLS_CHAOS;
  if (literacy < STRUCTURED_THRESHOLD) return SYMBOLS_STRUCTURED;
  return SYMBOLS_MARKS;
}

export function lettersKnown(literacy: number): ReadonlySet<string> {
  const n = Math.max(0, Math.floor(literacy / LETTER_COST));
  return new Set(LETTER_ORDER.slice(0, Math.min(n, LETTER_ORDER.length)));
}

export function knowsNumerals(literacy: number): boolean {
  return literacy >= NUMERAL_THRESHOLD;
}

function seededRng(seed: number): () => number {
  let s = seed === 0 ? 1 : seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h || 1;
}

/**
 * Render a word letter-by-letter. Known letters pass through; unknown
 * alphabetic characters become gibberish; non-alphabetic characters
 * pass through (so punctuation, spaces, dashes stay).
 */
export function renderWord(
  text: string,
  literacy: number,
  seedSalt?: string,
): string {
  const known = lettersKnown(literacy);
  const pool = symbolPoolFor(literacy);
  const rng = seededRng(hashString((seedSalt ?? '') + text));
  let out = '';
  for (const ch of text) {
    const lower = ch.toLowerCase();
    const isAlpha = lower >= 'a' && lower <= 'z';
    if (!isAlpha) {
      out += ch;
      continue;
    }
    if (known.has(lower)) {
      out += ch;
    } else {
      out += pool[Math.floor(rng() * pool.length)];
    }
  }
  return out;
}

/**
 * Render a numeric value. At low literacy, approximation words. At
 * high literacy, exact value.
 */
export function renderValue(value: number, literacy: number): string {
  if (knowsNumerals(literacy)) return value.toFixed(2);
  if (literacy >= 6) {
    if (value >= 1) return `~${Math.round(value)}`;
    return '~0';
  }
  if (literacy >= 3) {
    if (value >= 3) return 'plenty';
    if (value >= 1) return 'much';
    if (value >= 0.4) return 'some';
    return 'little';
  }
  if (literacy >= 1) {
    return value >= 1 ? 'more' : 'little';
  }
  // Pre-literate uncertainty narrows in lockstep with the symbol pool:
  // full chaos → structured marks → a single quiet mark → approximation words
  if (literacy >= STRUCTURED_THRESHOLD) return '.';
  if (literacy >= CHAOS_THRESHOLD + 0.2) return '?';
  if (literacy >= CHAOS_THRESHOLD) return '??';
  return '???';
}

export function renderLetterList(literacy: number): string {
  const ks = Array.from(lettersKnown(literacy));
  if (ks.length === 0) return '';
  return ks.join(' ');
}
